// Pricer handoff channel (IM-1955). Mounted ABOVE the layout so BOTH the
// Market Probabilities board and the Exotics Pricer page can reach it: the MP
// board writes a `pendingIntent` (a one-touch spec built from a clicked rung)
// and flips the GLOBAL page tab to the pricer; the pricer consumes the intent,
// seeds a single one-touch instrument into its reused "from Market
// Probabilities" tab, and clears the intent.
//
// This carries the INSTRUMENT, not a price — the pricer re-prices live via
// generic_pricer. No FE analytics cross the boundary.
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import { GlobalContext } from '../global-provider';
import { GlobalTabsKeysEnum } from '../models/custom-app-links';
import type { PayType } from '../../pages/exotics-pricer/contracts';

// The spec the MP board hands to the pricer. Pure data — a one-touch
// instrument description plus the model to pre-select.
export interface PricerHandoffSpec {
  // Registry-resolved vol-surface base_quote (e.g. 'XRP_USDC') — what the
  // pricer locks/prices on.
  token: string;
  // MP display token (e.g. 'XRP') — used only for the tab key + name, so the
  // per-token screen reads "Market Probabilities (XRP)".
  displayToken: string;
  contract: 'OneTouchUpper' | 'OneTouchLower';
  barrier: number;
  payType: PayType;
  notionalToken: 'base' | 'quote';
  // Bare "YYYY-MM-DD" — the format the pricer's instrument row expects
  // (ExpiryCell reads inst.expiry.slice(0,10)).
  expiry: string;
  model: 'bs' | 'lv' | 'sv';
  notional: number;
}

interface PricerHandoffContextType {
  pendingIntent: PricerHandoffSpec | null;
  // Set the intent AND switch the global page tab to the Exotics Pricer.
  openInPricer: (spec: PricerHandoffSpec) => void;
  clearIntent: () => void;
}

export const PricerHandoffContext = createContext<PricerHandoffContextType>(
  {} as PricerHandoffContextType,
);

export const PricerHandoffProvider = ({ children }: { children: ReactNode }) => {
  const { setCurrentTab } = useContext(GlobalContext);
  const [pendingIntent, setPendingIntent] = useState<PricerHandoffSpec | null>(null);

  const openInPricer = useCallback(
    (spec: PricerHandoffSpec) => {
      setPendingIntent(spec);
      setCurrentTab(GlobalTabsKeysEnum.pricing);
    },
    [setCurrentTab],
  );

  const clearIntent = useCallback(() => setPendingIntent(null), []);

  return (
    <PricerHandoffContext.Provider value={{ pendingIntent, openInPricer, clearIntent }}>
      {children}
    </PricerHandoffContext.Provider>
  );
};
